/**
 * AURA Content Adjustments Module
 * Handles font sizing, line height, letter spacing, readable font, etc.
 * Toggle-style options use icon buttons; continuous options use sliders.
 */

import { ContentSettings } from '../../core/types';
import { stateManager } from '../../core/state';
import { eventBus } from '../../core/events';
import { Logger } from '../../core/logger';
import { createSlider, createButtonGroup } from '../../ui/controls';
import { createIconButton } from '../../ui/controls/icon-button';
import { getIcon } from '../../ui/styles/icons';
import { applyContentStyles, enableTextMagnifier, disableTextMagnifier } from './effects';

const logger = new Logger('ContentModule');

interface ButtonRef {
  key: keyof ContentSettings;
  setChecked: (v: boolean) => void;
}

/**
 * Initialize the content adjustments module
 */
export function initContentModule(container: HTMLElement): void {
  logger.info('Initializing content module');

  const contentContainer = container.querySelector('#aura-content-adjustments');
  if (!contentContainer) {
    logger.warn('Content adjustments container not found');
    return;
  }

  const settings = stateManager.getSettings().content;
  const controls: HTMLElement[] = [];
  const buttonRefs: ButtonRef[] = [];

  // --- Sliders (continuous controls) ---

  controls.push(
    createSlider({
      id: 'aura-content-scaling',
      label: 'Content Scaling',
      value: settings.contentScaling,
      min: 100, max: 200, step: 10, unit: '%',
      onChange: (value) => updateSetting('contentScaling', value),
    }).element
  );

  controls.push(
    createSlider({
      id: 'aura-font-sizing',
      label: 'Adjust Font Sizing',
      value: settings.fontSizing,
      min: 12, max: 32, step: 2, unit: 'px',
      onChange: (value) => updateSetting('fontSizing', value),
    }).element
  );

  controls.push(
    createSlider({
      id: 'aura-line-height',
      label: 'Adjust Line Height',
      value: settings.lineHeight * 10,
      min: 10, max: 30, step: 1, unit: '', showValue: true,
      onChange: (value) => updateSetting('lineHeight', value / 10),
    }).element
  );

  controls.push(
    createSlider({
      id: 'aura-letter-spacing',
      label: 'Adjust Letter Spacing',
      value: settings.letterSpacing,
      min: 0, max: 5, step: 0.5, unit: 'px',
      onChange: (value) => updateSetting('letterSpacing', value),
    }).element
  );

  // --- Icon button grid (boolean toggles) – 2-col for 4 items = 2 even rows ---

  const toggleLabel = document.createElement('h4');
  toggleLabel.className = 'aura-panel__subsection-title';
  toggleLabel.textContent = 'Display Options';
  controls.push(toggleLabel);

  const grid = document.createElement('div');
  grid.className = 'aura-icon-btn-grid aura-icon-btn-grid--cols-2';

  const toggleDefs = buildToggleDefinitions(settings);
  for (const def of toggleDefs) {
    const btn = createIconButton({
      id: def.id,
      label: def.label,
      icon: getIcon(def.icon as any),
      checked: def.checked,
      onChange: def.onChange,
    });
    grid.appendChild(btn.element);
    buttonRefs.push({ key: def.settingKey, setChecked: btn.setChecked });
  }

  controls.push(grid);

  // --- Text Alignment (button group) ---

  const alignmentGroup = createButtonGroup({
    id: 'aura-text-alignment',
    label: 'Text Alignment',
    options: [
      { value: 'default', label: 'Default', icon: getIcon('alignDefault') },
      { value: 'left', label: 'Align Left', icon: getIcon('alignLeft'), ariaLabel: 'Align text left' },
      { value: 'center', label: 'Align Center', icon: getIcon('alignCenter'), ariaLabel: 'Align text center' },
      { value: 'right', label: 'Align Right', icon: getIcon('alignRight'), ariaLabel: 'Align text right' },
    ],
    value: settings.textAlignment,
    onChange: (value) =>
      updateSetting('textAlignment', value as ContentSettings['textAlignment']),
  });
  controls.push(alignmentGroup.element);

  // Render
  contentContainer.innerHTML = '';
  controls.forEach((c) => contentContainer.appendChild(c));

  // Keep controls synced with external changes (profile activation, reset)
  eventBus.on('settings:change', () => {
    const current = stateManager.getSettings().content;

    for (const ref of buttonRefs) {
      ref.setChecked(!!current[ref.key]);
    }

    alignmentGroup.setValue(current.textAlignment);
    applyContentStyles(current);

    if (current.textMagnifier) enableTextMagnifier();
    else disableTextMagnifier();
  });

  applyContentStyles(settings);
  logger.info('Content module initialized');
}

function updateSetting<K extends keyof ContentSettings>(
  key: K,
  value: ContentSettings[K]
): void {
  stateManager.updateSettings({
    content: { [key]: value } as Partial<ContentSettings>,
  });
}

interface ToggleDef {
  id: string;
  label: string;
  icon: string;
  checked: boolean;
  settingKey: keyof ContentSettings;
  onChange: (checked: boolean) => void;
}

function buildToggleDefinitions(s: ContentSettings): ToggleDef[] {
  return [
    {
      id: 'aura-readable-font',
      label: 'Readable Font',
      icon: 'readableFont',
      checked: s.readableFont,
      settingKey: 'readableFont',
      onChange: (checked) => updateSetting('readableFont', checked),
    },
    {
      id: 'aura-highlight-titles',
      label: 'Highlight Titles',
      icon: 'highlightTitles',
      checked: s.highlightTitles,
      settingKey: 'highlightTitles',
      onChange: (checked) => updateSetting('highlightTitles', checked),
    },
    {
      id: 'aura-highlight-links',
      label: 'Highlight Links',
      icon: 'highlightLinks',
      checked: s.highlightLinks,
      settingKey: 'highlightLinks',
      onChange: (checked) => updateSetting('highlightLinks', checked),
    },
    {
      id: 'aura-text-magnifier',
      label: 'Text Magnifier',
      icon: 'textMagnifier',
      checked: s.textMagnifier,
      settingKey: 'textMagnifier',
      onChange: (checked) => {
        updateSetting('textMagnifier', checked);
        if (checked) enableTextMagnifier();
        else disableTextMagnifier();
      },
    },
  ];
}
